import { FormProvider, useForm, useWatch } from "react-hook-form";

import { useBalanceQuery } from "@bentolabs/kit/hooks";
import invariant from "tiny-invariant";
import { parseUnits } from "viem";
import { useChainId } from "wagmi";

import { useUsdRate } from "entities/balance/use-usd-rate";
import { CurrencyLabel } from "entities/currency";
import { RHFInputCurrency } from "features/input-currency";
import { SelectCurrency } from "features/select-currency";
import {
  createLatestPricesSelect,
  useLatestPricesQuery,
} from "shared/api/use-latest-price-query";
import { bentoUSDConfig, Currency } from "shared/config/const";
import { Table } from "shared/ui/table";
import { catchError } from "shared/ui/toast";
import { adjustPriceUp } from "shared/utils/adjust-price";
import { useTrade } from "widgets/trade/context";
import { previewWithdraw } from "widgets/trade/utils";

import { WithdrawFormType } from "../../types";
import { LabelWithTabs } from "../label-with-tabs";
import { SubmitButton } from "../submit-button";
import { TransactionInfo } from "../transaction-info";
import { ResultsPreview } from "./results-preview";

type Props = {
  currencies: Currency[];
  onWithdrawSubmit: (data: WithdrawFormType, slippage: string) => Promise<void>;
};

export const WithdrawForm = (props: Props) => {
  const { theme } = useTrade();
  const { slippage } = useTrade();
  const { currencies, onWithdrawSubmit } = props;
  const chainId = useChainId();
  const latestPriceQuery = useLatestPricesQuery({
    select: createLatestPricesSelect(chainId, adjustPriceUp),
  });

  const bento = bentoUSDConfig[chainId];
  const form = useForm<WithdrawFormType>({
    mode: "onTouched",
    defaultValues: {
      type: "basket",
      fromCurrency: bento,
      fromValue: "",
      toValue: "",
      toCurrency: currencies[0],
    },
  });
  const {
    control,
    setValue,
    getValues,
    reset,
    trigger,
    handleSubmit,
    formState,
  } = form;
  const { isSubmitting } = formState;

  const fromCurrency = useWatch({ control, name: "fromCurrency" });
  const fromValue = useWatch({ control, name: "fromValue" });
  const toCurrency = useWatch({ control, name: "toCurrency" });
  const toValue = useWatch({ control, name: "toValue" });

  const fromCurrencyBalanceQuery = useBalanceQuery({ currency: fromCurrency });
  const toCurrencyBalanceQuery = useBalanceQuery({ currency: toCurrency });

  const toUsdValue = useUsdRate(toCurrency, toValue);

  const getAssetPrice = () => {
    invariant(latestPriceQuery.data, "latestPriceQuery.data is undefined");
    invariant(toCurrency?.symbol, "fromCurrency.symbol is undefined");
    console.log(latestPriceQuery.data);
    return latestPriceQuery.data[toCurrency.symbol];
  };

  const handleFromValueChange = (value: string) => {
    if (!value) {
      setValue("toValue", "");
      return;
    }

    const amount = parseUnits(value, fromCurrency.decimals);
    const price = getAssetPrice();

    invariant(toCurrency);

    const preview = previewWithdraw({
      amount,
      currency: toCurrency,
      priceAsset: price.formatted,
    });

    setValue("toValue", preview);
  };

  const handleFromCurrencyChange = (newCurrency: Currency) => {
    reset({
      fromCurrency: bento,
      fromValue: "",
      toValue: "",
      toCurrency: newCurrency,
    });
  };

  const handleWithdrawSubmit = async (data: WithdrawFormType) => {
    try {
      await onWithdrawSubmit(data, slippage);
      reset({
        ...getValues(),
        toValue: "",
        fromValue: "",
        type: "basket",
      });
    } catch (error) {
      catchError(error);
    }
  };

  const approvalCurrencies = [{ ...fromCurrency, value: toValue }];

  return (
    <FormProvider {...form}>
      <div className="flex flex-col px-2.5">
        <RHFInputCurrency
          label={
            <LabelWithTabs
              value={fromValue}
              currency={bento}
              onChange={(value) => {
                setValue("fromValue", value);
              }}
            >
              From
            </LabelWithTabs>
          }
          control={control}
          disabled={!fromCurrency}
          name="fromValue"
          bottomLabel="Balance"
          bottomValue={fromCurrencyBalanceQuery.data?.formatted}
          decimals={fromCurrency?.decimals}
          onChange={handleFromValueChange}
          slot={<CurrencyLabel disabled {...fromCurrency} />}
        />
        <RHFInputCurrency
          control={control}
          name="toValue"
          className="mt-2"
          label="From"
          usdValue={toUsdValue}
          bottomLabel="Balance"
          bottomValue={toCurrencyBalanceQuery.data?.formatted}
          decimals={toCurrency?.decimals}
          slot={
            <SelectCurrency
              currency={toCurrency}
              onChange={handleFromCurrencyChange}
              options={currencies}
            />
          }
        />

        <ResultsPreview
          className={`mt-6 ${theme === "spearmint" ? "text-spearmint" : "text-ultramarine-blue"}`}
        />

        <TransactionInfo
          className="mt-6.5"
          slot={<div>1 USDC = 1 bentoUSD</div>}
        >
          <div className="bg-nobel/5 flex flex-col px-4.5 py-4">
            <Table>
              <Table.Item label="Method:" value="Withdraw" />
            </Table>
          </div>
        </TransactionInfo>
      </div>

      <SubmitButton
        approvalCurrencies={approvalCurrencies}
        onApproveSuccess={() => {
          setTimeout(() => {
            trigger("fromValue");
          }, 200);
        }}
        variant={theme}
        spenderAddress={bentoUSDConfig[chainId].address}
        className="mt-6.5 w-full"
        size="56"
        isLoading={isSubmitting}
        onSubmit={handleSubmit(handleWithdrawSubmit, () => {
          catchError("Is not valid");
        })}
      >
        Withdraw
      </SubmitButton>
    </FormProvider>
  );
};
